// SPEC-31: the shell in English, key for key from pt-PT.ts; shell.ts holds
// the shape. Informal "you", no emoji, sentence case, curly apostrophes.
// The values she chooses (sports, levels, competition types) stay PT, read
// from pt-PT. The founder validates this text before prod.

import type { AssessmentId } from "../schema/types";
import { copy } from "./pt-PT";
import type { ShellCopy } from "./shell";
import { athleteLanguageEn } from "./admin/athleteLanguage";
import { bilanEn } from "./admin/bilan";
import { cycleEn } from "./admin/cycle";
import { bilanFormEn } from "./admin/bilanForm";
import { bodyMapEn } from "./admin/bodyMap";
import { newAccountEn } from "./admin/newAccount";

const ASSISTANT = copy.assistant.name;

/** Values the clinical team reads in one language: never translated. */
const values = copy.profileOnboarding;

/** What a run left mid-way still asks of her, in Conversa's resume. */
function leftToDo(left: number, unit: { one: string; many: string }): string {
  if (left === 0) return "All that’s left is to confirm your answers. Want to keep going?";
  if (left === 1) return `1 ${unit.one} left. Want to keep going?`;
  return `${left} ${unit.many} left. Want to keep going?`;
}

export const en: ShellCopy = {
  nav: {
    label: "Navigation",
    conversa: "Chat",
    assessments: "Assessments",
    profile: "Profile",
    openProfile: "Open your profile",
  },
  conversa: {
    loading: "Preparing the chat",
    failed: "Something went wrong on our side. Your answers are saved. Try again.",
    retry: "Try again",
    greetings: { morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening" },
    /** "Good morning, Mariana." or "Good morning." */
    hello: (greeting: string, firstName: string) => (firstName ? `${greeting}, ${firstName}.` : `${greeting}.`),
    welcome: (count: number, minutes: number) => [
      `Hi. I’m ${ASSISTANT}, I’ll be with you through your training.`,
      `Before we start, I need to get to know you. It’s ${count} questions, ${minutes} minutes.`,
    ],
    resume: (hello: string, left: number, unit: { one: string; many: string }) => [
      `${hello} You stopped halfway through an assessment.`,
      leftToDo(left, unit),
    ],
    next: (hello: string, remaining: number, minutes: number) => [
      remaining === 1
        ? `${hello} 1 assessment left to complete your health profile.`
        : `${hello} ${remaining} assessments left to complete your health profile.`,
      `The next one takes ${minutes} ${minutes === 1 ? "minute" : "minutes"}. Want to do it now?`,
    ],
    allDone: (hello: string) => [
      `${hello} You’ve answered every assessment.`,
      "There’s nothing to do for now. When there’s a new one, I’ll tell you here.",
    ],
    thanks: (firstName: string, title: string, assessmentId: AssessmentId, closing?: string) => {
      const thank = firstName ? `Thanks, ${firstName}.` : "Thanks.";
      if (closing === "sem-sintomas") return [`${thank} I’ve noted that you don’t feel any symptoms through your cycle.`];
      const done = `${thank} You’ve finished “${title}”.`;
      if (assessmentId === "ciclo-sintomas") return [done, "I’ll keep these symptoms in mind when I look at your cycle and your training load."];
      if (assessmentId === "ciclo-desporto") return [done, "I’ll put these answers together with your symptoms to understand your cycle better."];
      return [done, "Your answers are kept confidential in your health profile."];
    },
    units: {
      questions: { one: "question", many: "questions" },
      symptoms: { one: "symptom", many: "symptoms" },
      parts: { one: "part", many: "parts" },
    },
    todoKicker: (kicker: string) => `Assessment · ${kicker}`,
    todoMeta: (count: number, unit: string, minutes: number) => `${count} ${unit} · ${minutes} min · confidential`,
    progressKicker: (kicker: string) => `In progress · ${kicker}`,
    progressMeta: (answered: number, total: number) => `${answered} / ${total} answered · pick up where you left off`,
    start: "Start",
    resumeButton: "Continue",
    open: "Open the assessment",
    doneTitle: "All caught up",
    doneLine: (done: number) => `${done} assessments done`,
    doneCount: (done: number, total: number) => `${done} / ${total}`,
    toAssessments: "See my assessments",
    composer: `Message ${ASSISTANT}…`,
    send: "Send",
  },
  splash: {
    loading: "Opening PYF",
  },
  welcome: {
    title: "Train more, with less pain.",
    lead: "PYF tracks your pain, training load and cycle, and suggests the next step.",
    signUp: "Create account",
    signIn: "I have an account · Sign in",
    language: "Language",
  },
  signUp: {
    back: "Back",
    signIn: "Sign in",
    title: "Create your account.",
    lead: "It takes less than a minute.",
    firstNameLabel: "What’s your name?",
    firstNamePlaceholder: "First name",
    emailLabel: "Email",
    emailPlaceholder: "name@email.com",
    passwordLabel: "Password",
    passwordPlaceholder: "At least 8 characters",
    passwordHint: "8 characters · 1 number",
    submit: "Continue",
    submitting: "Creating…",
    errors: {
      firstNameEmpty: "Tell me your name.",
      firstNameTooLong: "Use no more than 40 characters.",
      emailInvalid: "Check the email address.",
      emailInUse: "There’s already an account with this email.",
      passwordInvalid: "It needs 8 characters and 1 number.",
      failed: "I couldn’t create the account. Try again.",
    },
    // "By continuing, you accept the Terms and the Privacy policy. Your
    // health data stays yours.", cut around its two links.
    legal: {
      before: "By continuing, you accept the ",
      terms: "Terms",
      between: " and the ",
      privacy: "Privacy policy",
      after: ". Your health data stays yours.",
    },
  },
  signIn: {
    back: "Back",
    signUp: "Create account",
    title: "Welcome back.",
    lead: "Sign in to pick up where you left off.",
    emailLabel: "Email",
    emailPlaceholder: "name@email.com",
    passwordLabel: "Password",
    forgot: "I forgot my password",
    // "We sent an email to {email}.", cut around the address.
    resetSent: { before: "We sent an email to ", after: "." },
    submit: "Sign in",
    submitting: "Signing in…",
    or: "or",
    google: "Continue with Google",
    errors: {
      wrongCredentials: "Wrong password. Try again.",
      tooManyAttempts: "Too many attempts. Try again in 10 minutes.",
      offline: "No connection. Try again.",
      emailFirst: "Enter your email first.",
      accountExists: "You already have an account with this email. Sign in with your password.",
      resetFailed: "I couldn’t send the email. Try again.",
      failed: "I couldn’t sign you in. Try again.",
    },
  },
  newPassword: {
    back: "Back",
    title: "Choose a new password.",
    lead: "Then you sign in with it.",
    label: "New password",
    placeholder: "At least 8 characters",
    hint: "8 characters · 1 number",
    submit: "Save",
    submitting: "Saving…",
    doneTitle: "Password saved.",
    doneLead: "You can now sign in with your new password.",
    invalidTitle: "This link no longer works.",
    invalidLead: "Each link lasts 1 hour and works only once. Ask for a new one on the Sign in screen.",
    signIn: "Sign in",
    errors: {
      passwordInvalid: "It needs 8 characters and 1 number.",
      offline: "No connection. Try again.",
      failed: "I couldn’t save the password. Try again.",
    },
  },
  profileOnboarding: {
    back: "Back",
    part: "Part 1 · General info",
    typing: "Lena is typing",
    send: "Send",
    greeting: "Hi. I’m Lena, I’ll be with you through your training.",
    firstName: ["Before we start, I need to get to know you. What’s your name?"],
    firstNamePlaceholder: "Your first name",
    birthDate: (name: string) => [`Nice to meet you, ${name}. What’s your date of birth?`],
    birthDateAgain: "What’s your date of birth?",
    birthDatePlaceholder: "dd/mm/yyyy",
    dateUnreadable: "I couldn’t read the date. Write it like this: 14/03/2001.",
    tooYoung: (name: string, minAge: number) => [
      `Thanks, ${name}. PYF is for athletes aged ${minAge} and over.`,
      `So we’ll stop here. I didn’t save your date of birth. When you turn ${minAge}, I’ll be here waiting for you.`,
    ],
    leave: "Leave",
    modality: (age: number) => [`You’re ${age}.`, "What’s your sport?"],
    modalityChips: values.modalityChips,
    modalityOther: "Other",
    modalityOtherPlaceholder: "Type your sport",
    orWriteHere: "Or type here…",
    position: ["What’s your position, event or weight class?"],
    positionPlaceholder: "E.g. midfielder, 400 m, −60 kg",
    level: ["What level do you compete at?"],
    levelChips: values.levelChips,
    levelOther: "Other",
    levelOtherPlaceholder: "Type your level",
    club: ["Which club do you train with?"],
    noClub: "No club",
    clubPlaceholder: "Club name",
    load: ["How many training sessions do you do a week, and how many hours in total?"],
    trainings: "Sessions per week",
    hours: "Hours per week",
    less: "Less",
    more: "More",
    confirm: "Confirm",
    // The card: her load is a value, kept in PT like the sport and the level (« 4 treinos · 6 h por semana »).
    loadAnswer: values.loadAnswer,
    competition: ["What does your competition look like?"],
    competitionOptions: {
      league: { letter: "A", title: values.competitionOptions.league.title, hint: "Team sports" },
      events: { letter: "B", title: values.competitionOptions.events.title, hint: "Individual or combat sports" },
    },
    closing: (name: string) => [
      `Thanks, ${name}. Part 1 is done.`,
      "Here’s the summary. You can change any of it later in your profile.",
    ],
    saveFailed: "I couldn’t save your details. Check your connection and try again.",
    retry: "Try again",
    summary: {
      title: "Part 1 done",
      name: "Name",
      birth: "Born",
      birthValue: (date: string, age: number) => `${date} · ${age} years old`,
      modality: "Sport",
      position: "Position",
      laterality: "Laterality",
      level: "Level",
      club: "Club",
      load: "Training load",
      competition: "Competition",
    },
    toAssessments: "See my assessments",
    toConversa: "Back to chat",
    toProfile: "Back to profile",
    leaveEdit: "Stop editing",
  },
  assessments: {
    today: (weekday: string, day: number, month: string) => `Today · ${weekday} ${day} ${month}`,
    // Sunday first, as Date.getDay() counts.
    weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    title: "Your assessments.",
    loading: "Loading your assessments",
    offline: "No connection",
    failed: "Something went wrong on our side. Your answers are saved. Try again.",
    retry: "Try again",
    completeProfile: "Complete your general info",
    profileLine: (age: number, modality: string, position: string) => `${age} years old · ${modality} · ${position}`,
    doneLabel: "Done",
    left: (remaining: number, minutes: number) =>
      remaining === 1
        ? `1 assessment left · about ${minutes} min`
        : `${remaining} assessments left · about ${minutes} min`,
    allDone: "All assessments done.",
    groups: { progress: "In progress", todo: "To do", done: "Done" },
    tagTodo: "To do",
    tagDone: "✓ Done",
    tagProgress: (answered: number, total: number) => `${answered} / ${total}`,
    units: { questions: "questions", symptoms: "symptoms", parts: "parts" },
    todoMeta: (kicker: string, count: number, unit: string, minutes: number) => `${kicker} · ${count} ${unit} · ${minutes} min`,
    progressMeta: (kicker: string) => `${kicker} · in progress`,
    doneMeta: (kicker: string, day: number, month: string, editable: boolean) =>
      `${kicker} · done on ${day} ${month} · ${editable ? "editable" : "read-only"}`,
    // The letters and the instruments' kickers stay as in PT; only
    // « Perfil » and « Ciclo menstrual » are words to translate.
    rows: {
      dados: { letter: "P", title: "General info", kicker: "Profile" },
      iciq: { letter: "I", title: "Bladder leaks", kicker: "ICIQ-UI SF" },
      "ciclo-sintomas": { letter: "C", title: "Your period: symptoms", kicker: "Menstrual cycle" },
      "ciclo-desporto": { letter: "C", title: "Your period and sport", kicker: "Menstrual cycle" },
      leafq: { letter: "L", title: "Injuries, digestion and cycle", kicker: "LEAF-Q" },
      sono: { letter: "S", title: "Sleep", kicker: "ASBQ" },
      beda: { letter: "B", title: "Food and body image", kicker: "BEDA-Q" },
      ciss: { letter: "O", title: "Eyes and vision", kicker: "CISS" },
      fonseca: { letter: "M", title: "Jaw and chewing", kicker: "Fonseca" },
    },
  },
  profile: {
    title: "Profile",
    loading: "Loading your profile",
    generalData: "General info",
    edit: "Edit",
    answer: "Answer",
    notAnswered: "You haven’t answered yet.",
    rows: {
      birth: "Date of birth",
      modality: "Sport",
      position: "Position",
      laterality: "Laterality",
      level: "Level",
      club: "Club",
      load: "Training load",
      competition: "Competition",
    },
    empty: "—",
    privacyTitle: "Your health data is confidential",
    privacyText: "It isn’t shared with your club or your coach.",
    signOut: "Sign out",
    sending: "Sending your answers…",
    confirmTitle: "Sign out?",
    unsent: "Some answers haven’t been sent yet. If you sign out now, you’ll lose them.",
    confirm: "Sign out",
    cancel: "Cancel",
    leaving: "Signing out…",
    signOutFailed: "I couldn’t sign you out. Try again.",
    language: {
      title: "Language",
      line: "Applies to the whole app.",
      saveFailed: "I couldn’t save the language to your account. It stays on this phone and I’ll try again.",
    },
  },
  legalNotice: {
    onlyPortuguese: "This text is only in Portuguese for now.",
  },
  placeholder: {
    soon: "This screen is still being built.",
    back: "Back to assessments",
  },
  // SPEC-38: the PYF team's admin (SPEC-33, SPEC-34), informal "you" as in
  // PT. The screen sets the labels in capitals.
  admin: {
    language: "Language",
    skip: "Skip to the user base",
    label: "Admin · confidential health data",
    sections: "Admin sections",
    base: "Database",
    profile: "Profile",
    toApp: "App",
    title: "User base.",
    signInLead: "Sign in with your PYF team account.",
    denied: "You don’t have access to this area.",
    signOut: "Sign out",
    signOutFailed: "I couldn’t sign you out. Try again.",
    loading: "Loading the user base…",
    failed: "I couldn’t load the user base. Try again.",
    retry: "Try again",
    searchLabel: "Search users",
    search: "Search by name, email or club…",
    export: "Export CSV",
    exporting: "Exporting…",
    exportFailed: "I couldn’t export. Try again.",
    stats: {
      accounts: "Users",
      newAccounts: (n: number) => (n === 1 ? "1 new in the last 30 days" : `${n} new in the last 30 days`),
      done: "Assessments done",
      possible: (possible: number) => `/ ${possible}`,
      /** English writes the percent sign with no space. */
      donePercent: (percent: number) => `${percent}% of the possible total`,
      inProgress: "In progress",
      /** One form in English, singular or plural. */
      halfway: (_n: number) => "left halfway",
      alerts: "With an alert",
      aboveThreshold: "result above the threshold",
    },
    count: (n: number) => (n === 1 ? "1 user" : `${n} users`),
    filtersLabel: "Filter users",
    filters: { all: "All", alert: "With an alert", progress: "In progress", inactive: "Inactive" },
    caption: "PYF users",
    columns: {
      user: "User",
      club: "Club · sport",
      level: "Level",
      assessments: "Assessments",
      dates: "Created · last activity",
      status: "Status",
    },
    none: "—",
    noClub: "No club",
    detail: (parts: readonly string[]) => parts.join(" · "),
    years: (age: number) => `${age} years old`,
    of: (total: number) => `/ ${total}`,
    date: (day: number, month: string) => `${day} ${month}`,
    statuses: { alert: "Alert", inactive: "Inactive", progress: "In progress", complete: "✓ Complete", active: "Active" },
    empty: "No users match.",
    more: "Show more",
    athlete: {
      skip: "Skip to the profile",
      back: "User base",
      loading: "Loading the profile…",
      failed: "I couldn’t load the profile. Try again.",
      notFound: "This user doesn’t exist.",
      meta: (parts: readonly string[]) => parts.join(" · "),
      created: (date: string) => `account created on ${date}`,
      activity: (date: string) => `last active on ${date}`,
      export: "Export answers",
      exporting: "Exporting…",
      exportFailed: "I couldn’t export. Try again.",
      stats: {
        done: "Assessments done",
        toDo: (n: number) => (n === 0 ? "health profile complete" : `${n} to do`),
        inProgress: "In progress",
        halfway: (n: number) => (n === 0 ? "none left halfway" : "left halfway"),
        alerts: "Alerts",
        instruments: (kickers: readonly string[]) => kickers.join(" · "),
        noAlert: "no result above the threshold",
        since: "Since sign-up",
        days: (n: number) => (n === 1 ? "day" : "days"),
        lastActivity: (days: number) => {
          if (days === 0) return "last active today";
          if (days === 1) return "last active 1 day ago";
          return `last active ${days} days ago`;
        },
      },
      generalData: "General info",
      notAnswered: "She hasn’t answered yet.",
      privacyTitle: "Confidential health data",
      privacyText: "Visible to the clinical team only. Never shared with the club or the coach.",
      assessments: "Assessments and results",
      doneMeta: (kicker: string, date: string) => `${kicker} · done on ${date}`,
      progressMeta: (kicker: string, answered: number, total: number) => `${kicker} · ${answered} / ${total} answered`,
      todoMeta: (kicker: string) => `${kicker} · to do`,
      tags: { done: "✓ Done", progress: "In progress", todo: "To do", alert: "Alert" },
      max: (max: number) => `/${max}`,
      result: (score: number, max: number, threshold: number | undefined, alert: boolean) => {
        const parts = [`Score ${score} / ${max}`];
        if (threshold !== undefined) parts.push(`threshold ${threshold}`);
        if (alert) parts.push("above the threshold");
        return parts.join(" · ");
      },
      noScore: "No score",
    },
    // SPEC-44 to SPEC-51: the bilan presencial, its body map and its form sections.
    bilan: bilanEn,
    cycle: cycleEn,
    bodyMap: bodyMapEn,
    bilanForm: bilanFormEn,
    athleteLanguage: athleteLanguageEn,
    newAccount: newAccountEn,
  },
};
